import { useMessagePlacementHooks } from '@atlassian/post-office-context';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import type { MessageProps } from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { useCallback, useEffect, useState } from 'react';

import { MessageRenderer, type renderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withJiraTopBannerIntlProvider } from '../__generated__/intl-provider';
import { type JiraTopBannerInitialData } from '../api/types';
import { type Placement as JiraTopBannerPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const JiraTopBannerPlacementComponent: React.ComponentType<any> =
	withJiraTopBannerIntlProvider(
		createPlacementComponent<JiraTopBannerPlacement>(
			PLACEMENT_ID,
			({ initialData }) => {
				const { data, isLoading } = useInitialData<JiraTopBannerInitialData>(initialData);
				// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-unsafe-assignment
				const [{ message, status }, setMessageState] = useState<{
					// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
					message: Message | undefined;
					status: 'loading' | 'done';
				}>({
					message: undefined,
					status: 'loading',
				});

				const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

				const handleDismiss = useCallback(() => {
					setMessageState({ message: undefined, status: 'done' });
				}, []);

				useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss, handleDismiss]);
				useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked, handleDismiss]);
				useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze, handleDismiss]);

				useEffect(() => {
					if (!isLoading) {
						const newMessage = data?.messages[0];

						setMessageState({
							message: newMessage,
							status: 'done',
						});
					}
				}, [data, isLoading]);

				if (isLoading || status === 'loading') {
					return null;
				}

				// eslint-disable-next-line @typescript-eslint/no-unsafe-return
				return (
					message && (
						<MessageRenderer
							{...message}
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
							messageInstanceId={message.messageInstanceId}
						/>
					)
				);
			},
			{
				choreographer: {
					isDefaultEnabled: false,
				},
			},
		),
	);

export default JiraTopBannerPlacementComponent;
